import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import {
  createAddress,
  createContact,
  createOrderSaleItem,
  createShippingType,
  editAddress,
  editContact,
  editOrderSaleItem,
  editShippingType,
  loadAddress,
  loadAddresses,
  loadContact,
  loadContacts,
  loadOrderSaleItem,
  loadOrderSaleItems,
  loadOrderSales,
  loadShippingType,
  loadShippingTypes,
  removeAddress,
  removeContact,
  removeOrderSaleItem,
  removeShippingType,
} from '@sophocles/features/warehouse/orders/api';
import {
  AddressSchema,
  ContactSchema,
  OrderSaleItemSchema,
  OrderSaleSchema,
  ShippingTypeSchema,
} from '@sophocles/features/warehouse/orders/schema';
import { createEntitySlice } from '@sophocles/utils/slice';
import { createViewReducers, initialViewState } from '@sophocles/utils/viewState';

const addressSlice = createEntitySlice(
  'addresses',
  AddressSchema,
  loadAddresses,
  loadAddress,
  createAddress,
  editAddress,
  removeAddress,
  'warehouse',
);
const contactSlice = createEntitySlice(
  'contacts',
  ContactSchema,
  loadContacts,
  loadContact,
  createContact,
  editContact,
  removeContact,
  'warehouse',
);
// OrderSale single-entity reads/writes (fetchOne/create/patch/delete) migrated
// to RTK Query (features/entities/orderSales.js); the slice keeps only the list
// read-state (loadOrderSales) and the grid's view/filter state. Built inline
// rather than via createEntitySlice — that factory requires the now-removed
// single-entity thunks for its create/edit/remove/loadOne extraReducers.
const orderSaleAdapter = createEntityAdapter();
const orderSaleReduxSlice = createSlice({
  name: 'orderSales',
  initialState: orderSaleAdapter.getInitialState({
    view: initialViewState,
  }),
  reducers: {
    ...createViewReducers(),
  },
  extraReducers: builder => {
    builder.addCase(loadOrderSales.fulfilled, (state, { payload }) => {
      if (payload && payload.length > 0) {
        const { entities } = normalize(payload, [OrderSaleSchema]);
        orderSaleAdapter.upsertMany(state, Object.values(entities.orderSales));
        state.view.lastLoaded = new Date().toISOString();
      }
    });
  },
});

const selectOrderSaleEntities = state => state.warehouse.orderSales;
const { selectById: selectOrderSaleById, selectAll: selectAllOrderSales } =
  orderSaleAdapter.getSelectors(selectOrderSaleEntities);
const selectOrderSaleView = state => state.warehouse.orderSales?.view;

const orderSaleSlice = {
  reducer: orderSaleReduxSlice.reducer,
  actions: { ...orderSaleReduxSlice.actions },
  selectors: {
    selectEntityById: selectOrderSaleById,
    selectAllEntities: selectAllOrderSales,
    selectPage: state => selectOrderSaleView(state)?.page,
    selectPageSize: state => selectOrderSaleView(state)?.pageSize,
    selectSearch: state => selectOrderSaleView(state)?.search,
    selectStart: state => selectOrderSaleView(state)?.start,
    selectEnd: state => selectOrderSaleView(state)?.end,
    selectFilters: state => selectOrderSaleView(state)?.filters,
    selectLastLoaded: state => selectOrderSaleView(state)?.lastLoaded,
    selectIsLoading: state => selectOrderSaleView(state)?.loading,
  },
};
const orderSaleItemSlice = createEntitySlice(
  'orderSaleItems',
  OrderSaleItemSchema,
  loadOrderSaleItems,
  loadOrderSaleItem,
  createOrderSaleItem,
  editOrderSaleItem,
  removeOrderSaleItem,
  'warehouse',
);
const shippingTypeSlice = createEntitySlice(
  'shippingTypes',
  ShippingTypeSchema,
  loadShippingTypes,
  loadShippingType,
  createShippingType,
  editShippingType,
  removeShippingType,
  'warehouse',
);

export { addressSlice, contactSlice, orderSaleSlice, orderSaleItemSlice, shippingTypeSlice };
